import React from 'react';
import { Heading } from '@chakra-ui/react';
import { AlertCircle, Network } from 'lucide-react';
import { Separator } from 'src/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
} from 'src/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import DataModal from './request-details/data-modal';
import {
  getSelectedNetworkIndex,
  getSelectedNetworkRequest,
} from 'src/redux/providerBuilder/selectedNetworkRequest/selectors';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { setSelectedNetworkIndex } from 'src/redux/providerBuilder/selectedNetworkRequest';
import { setSelectedInstances } from 'src/redux/providerBuilder/selectedInstances';
import { getSelectedInstances } from 'src/redux/providerBuilder/selectedInstances/selectors';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical } from 'lucide-react';

const SelectedRequests = () => {
  const selectedInstances = useAppSelector(getSelectedInstances);
  const getAllSelectedRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);

  const dispatch = useAppDispatch();

  const formatUrlName = (url: string) => {
    if (!url) return '';

    // Split the URL by '/' and get the last non-empty segment
    const segments = url.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : url;
  };

  console.log({ selectedInstances });

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      return; // Prevent cross-requestIndex dropping
    }

    const req_index = parseInt(source.droppableId.split('-')[1], 10);
    const filteredInstances = selectedInstances.filter(
      (instance) => instance.responseSelection.requestIndex === req_index
    );

    const newInstances = Array.from(filteredInstances);
    const [reorderedInstance] = newInstances.splice(source.index, 1);
    newInstances.splice(destination.index, 0, reorderedInstance);

    // Update order property
    const updatedInstances = newInstances.map((instance, index) => ({
      ...instance,
      responseSelection: {
        ...instance.responseSelection,
        order: index,
      },
    }));

    // Update the original selectedInstances array
    const finalUpdatedInstances = selectedInstances.map((instance) => {
      if (instance.responseSelection.requestIndex === req_index) {
        return updatedInstances.shift() || instance;
      }
      return instance;
    });

    console.log({ finalUpdatedInstances, newInstances });

    dispatch(setSelectedInstances([...finalUpdatedInstances]));

    // Dispatch or update state with updatedInstances
    // dispatch({ type: 'UPDATE_SELECTED_INSTANCES', payload: updatedInstances }); // Replace with your actual dispatch/state update logic
  };

  console.log(selectedInstances);

  return (
    // <Sidebar className=" top-0 left-0 absolute h-[80vh] overflow-y-auto">
    <Sidebar className=" top-0 left-0 absolute h-[80vh]">
      <SidebarHeader>
        <Heading size="md" className=" py-2 flex items-center gap-2">
          <Network className="w-4 h-4 text-gray-600 shrink-0" />
          Selected Requests
        </Heading>
      </SidebarHeader>

      <SidebarContent>
        <SidebarMenu className="flex flex-col gap-2 pt-2">
          {getAllSelectedRequest
            ?.filter((request) => typeof request === 'object' && request.currentPageUrl)
            ?.map((request: any, req_index: number) => (
              <React.Fragment key={req_index}>
                <Collapsible
                  className="group/collapsible"
                  open={req_index === selectedNetworkIndex}
                  onOpenChange={() => {
                    dispatch(setSelectedNetworkIndex(req_index));
                  }}
                >
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      className={`font-semibold ${req_index === selectedNetworkIndex ? 'bg-gray-100' : ''} rounded-none`}
                    >
                      <span className="flex items-center gap-2">
                        <p className="text-[8px] rounded p-[2px] px-[4px] bg-green-100 text-green-900">
                          {request.method}
                        </p>
                        <span className="text-xs">
                          {formatUrlName(request.url).length > 40
                            ? `/${formatUrlName(request.url).substring(0, 40)}... ${formatUrlName(request.url).slice(-40)}`
                            : '/' + formatUrlName(request.url)}
                        </span>
                      </span>
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId={`droppable-${req_index}`}>
                      {(provided) => (
                        <CollapsibleContent
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="flex flex-col pl-4 gap-2 p-2 "
                        >
                          {selectedInstances
                            .filter(
                              (instance) =>
                                instance.responseSelection.requestIndex === req_index
                            )
                            .map((instance, instance_index) => (
                              <Draggable
                                key={instance.variableName}
                                draggableId={instance.variableName}
                                index={instance_index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      pointerEvents: 'auto',
                                    }}
                                  >
                                    <div className="flex justify-between items-center">
                                      <div
                                        {...provided.dragHandleProps}
                                        className="cursor-grab"
                                      >
                                        <GripVertical className="w-3 h-3 text-gray-400" />
                                      </div>

                                      <DataModal
                                        instance={instance}
                                        instance_index={instance_index}
                                      />
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}

                          {provided.placeholder}
                          {!selectedInstances.some(
                            (instance) =>
                              instance.responseSelection.requestIndex === req_index
                          ) && (
                            <div className="flex items-center gap-2 p-2 text-xs text-gray-500 bg-gray-50 rounded">
                              <AlertCircle className="w-3 h-3 shrink-0" />
                              <span>Empty</span>
                            </div>
                          )}
                        </CollapsibleContent>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Collapsible>
                <Separator />
              </React.Fragment>
            ))}
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  );
};

export default SelectedRequests;
